import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash, X, FileSpreadsheet, Users, Search, LayoutGrid, List } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import JugadoresDialog from './JugadoresDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClubesTab({ ligaId, ligaName }) {
  const [clubes, setClubes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [name, setName] = useState('');
  const [selectedCategorias, setSelectedCategorias] = useState([]);
  const [logo, setLogo] = useState('');
  const [loading, setLoading] = useState(false);
  const [showJugadores, setShowJugadores] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');

  useEffect(() => {
    if (ligaId) {
      loadData();
    }
  }, [ligaId]);

  const loadData = async () => {
    try {
      const [clubesRes, categoriasRes] = await Promise.all([
        axios.get(`${API}/clubes?liga_id=${ligaId}`, { withCredentials: true }),
        axios.get(`${API}/categorias?liga_id=${ligaId}`, { withCredentials: true })
      ]);
      setClubes(clubesRes.data);
      setCategorias(categoriasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (selectedCategorias.length === 0) {
      toast.error('Debes agregar al menos una categoría');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name,
        liga_id: ligaId,
        categorias: selectedCategorias,
        logo: logo || null
      };
      if (editingClub) {
        await axios.put(`${API}/clubes/${editingClub.id}`, payload, { withCredentials: true });
        toast.success('Club actualizado');
      } else {
        await axios.post(`${API}/clubes`, payload, { withCredentials: true });
        toast.success('Club creado');
      }
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar club');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setSelectedCategorias([]);
    setLogo('');
    setEditingClub(null);
  };

  const handleEdit = (club) => {
    setEditingClub(club);
    setName(club.name);
    setSelectedCategorias(club.categorias);
    setLogo(club.logo || '');
    setShowDialog(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamaño (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('La imagen no debe superar 2MB');
        return;
      }
      
      // Validar tipo
      if (!file.type.startsWith('image/')) {
        toast.error('Solo se permiten imágenes');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = async (clubId) => {
    if (!window.confirm('¿Estás seguro de eliminar este club?')) return;
    try {
      await axios.delete(`${API}/clubes/${clubId}`, { withCredentials: true });
      toast.success('Club eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar club');
    }
  };

  const addCategoria = (categoriaId, nombreCorto) => {
    if (!categoriaId || !nombreCorto) return;
    if (selectedCategorias.find(c => c.categoria_id === categoriaId)) {
      toast.error('Esta categoría ya fue agregada');
      return;
    }
    setSelectedCategorias([...selectedCategorias, { categoria_id: categoriaId, nombre_corto: nombreCorto }]);
  };

  const removeCategoria = (categoriaId) => {
    setSelectedCategorias(selectedCategorias.filter(c => c.categoria_id !== categoriaId));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Clubes</h2>
          <p className="text-sm text-slate-400">Liga: {ligaName}</p>
        </div>
        <Button data-testid="create-club-btn"
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="gap-2 bg-white text-slate-900 hover:bg-slate-200">
          <Plus className="w-4 h-4" /> Nuevo Club
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar club..." className="pl-9 bg-slate-700 border-slate-600 text-white"
            data-testid="clubes-search" />
        </div>
        <div className="flex border border-slate-600 rounded-lg overflow-hidden">
          <button onClick={() => setViewMode('cards')}
            className={`p-2 ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}
            data-testid="clubes-view-cards"><LayoutGrid className="w-4 h-4" /></button>
          <button onClick={() => setViewMode('list')}
            className={`p-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}
            data-testid="clubes-view-list"><List className="w-4 h-4" /></button>
        </div>
      </div>

      {(() => {
        const filtered = clubes.filter(c =>
          !searchTerm || c.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (filtered.length === 0) {
          return <div className="text-center py-12 text-slate-500">{searchTerm ? 'Sin resultados' : 'No hay clubes creados para esta liga'}</div>;
        }

        if (viewMode === 'list') {
          return (
            <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-slate-700 text-slate-400 text-xs uppercase">
                  <th className="px-4 py-3 text-left">Club</th>
                  <th className="px-4 py-3 text-left">Categorias</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr></thead>
                <tbody>
                  {filtered.map(club => (
                    <tr key={club.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {club.logo ? <img src={club.logo} alt="" className="w-8 h-8 rounded object-cover" /> : <FileSpreadsheet className="w-5 h-5 text-slate-500" />}
                          <span className="text-white font-medium">{club.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {club.categorias?.map(cat => <span key={cat.categoria_id} className="px-1.5 py-0.5 bg-slate-700 text-slate-300 text-xs rounded">{cat.nombre_corto}</span>)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button onClick={() => { setSelectedClub(club); setShowJugadores(true); }} variant="outline" size="sm" className="h-7 px-2 border-slate-600 text-slate-300"><Users className="w-3 h-3" /></Button>
                          <Button onClick={() => handleEdit(club)} variant="outline" size="sm" className="h-7 px-2 border-slate-600 text-slate-300"><Edit className="w-3 h-3" /></Button>
                          <Button onClick={() => handleDelete(club.id)} variant="destructive" size="sm" className="h-7 px-2"><Trash className="w-3 h-3" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((club) => (
            <Card key={club.id} className="p-4 hover:shadow-md transition-shadow bg-slate-800 border-slate-700" data-testid="club-card">
              <div className="flex items-start gap-3 mb-3">
                {club.logo ? (
                  <img 
                    src={club.logo} 
                    alt={club.name}
                    className="w-16 h-16 object-cover rounded-lg border-2 border-slate-600"
                  />
                ) : (
                  <div className="w-16 h-16 bg-slate-700 rounded-lg flex items-center justify-center">
                    <FileSpreadsheet className="w-8 h-8 text-slate-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg mb-1 text-white">{club.name}</h3>
                  <div className="flex flex-wrap gap-1">
                    {club.categorias.map((cat) => (
                      <span key={cat.categoria_id} className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded">
                        {cat.nombre_corto}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            <div className="flex gap-2 mt-3">
              <Button
                onClick={() => {
                  setSelectedClub(club);
                  setShowJugadores(true);
                }}
                variant="outline"
                size="sm"
                className="gap-2 flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Users className="w-4 h-4" />
                Jugadores
              </Button>
              <Button
                data-testid={`edit-club-${club.id}`}
                onClick={() => handleEdit(club)}
                variant="outline"
                size="sm"
                className="gap-2 flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Edit className="w-4 h-4" />
                Editar
              </Button>
              <Button
                data-testid={`delete-club-${club.id}`}
                onClick={() => handleDelete(club.id)}
                variant="destructive"
                size="sm"
                className="gap-2 flex-1"
              >
                <Trash className="w-4 h-4" />
                Eliminar
              </Button>
            </div>
          </Card>
          ))
        }
      </div>
        );
      })()}

      {/* Dialog for create/edit */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="club-dialog" className="max-w-2xl bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">{editingClub ? 'Editar Club' : 'Nuevo Club'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label className="text-slate-300" htmlFor="name">Nombre del Club</Label>
              <Input
                data-testid="club-name-input"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300" htmlFor="logo">Logo del Club (opcional)</Label>
              <div className="flex items-center gap-4">
                {logo && (
                  <img 
                    src={logo} 
                    alt="Preview" 
                    className="w-20 h-20 object-cover rounded-lg border-2 border-slate-600"
                  />
                )}
                <div className="flex-1">
                  <Input
                    data-testid="club-logo-input"
                    id="logo"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="cursor-pointer bg-slate-700 border-slate-600 text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">Max 2MB - PNG, JPG, JPEG</p>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Liga</Label>
              <Input value={ligaName} disabled className="bg-slate-700 border-slate-600 text-slate-400" />
            </div>
            <div>
              <Label className="text-slate-300">Categorias</Label>
              <div className="space-y-2">
                {selectedCategorias.map((cat) => (
                  <div key={cat.categoria_id} className="flex items-center gap-2 p-2 bg-slate-700 rounded">
                    <div className="flex-1">
                      <p className="text-xs text-slate-400 mb-1">
                        {categorias.find(c => c.id === cat.categoria_id)?.name}
                      </p>
                      <Input
                        value={cat.nombre_corto}
                        onChange={(e) => {
                          const updated = selectedCategorias.map(c => 
                            c.categoria_id === cat.categoria_id 
                              ? { ...c, nombre_corto: e.target.value }
                              : c
                          );
                          setSelectedCategorias(updated);
                        }}
                        placeholder="Nombre corto"
                        className="h-8 bg-slate-600 border-slate-500 text-white"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCategoria(cat.categoria_id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <AddCategoriaForm categorias={categorias} onAdd={addCategoria} />
              </div>
            </div>
            <Button data-testid="club-save-btn" type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading}>
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Jugadores */}
      {showJugadores && selectedClub && (
        <JugadoresDialog 
          club={selectedClub} 
          onClose={() => {
            setShowJugadores(false);
            setSelectedClub(null);
          }} 
        />
      )}
    </div>
  );
}

function AddCategoriaForm({ categorias, onAdd }) {
  const [selectedCategoria, setSelectedCategoria] = useState('');
  const [nombreCorto, setNombreCorto] = useState('');

  const handleAdd = () => {
    if (selectedCategoria && nombreCorto) {
      onAdd(selectedCategoria, nombreCorto);
      setSelectedCategoria('');
      setNombreCorto('');
    }
  };

  if (!categorias || categorias.length === 0) {
    return (
      <div className="text-sm text-slate-500 text-center py-4">
        No hay categorias disponibles. Crea categorias primero.
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <Select value={selectedCategoria || undefined} onValueChange={setSelectedCategoria}>
        <SelectTrigger data-testid="add-categoria-select" className="flex-1 bg-slate-700 border-slate-600 text-white">
          <SelectValue placeholder="Selecciona categoria" />
        </SelectTrigger>
        <SelectContent position="popper" sideOffset={5}>
          {categorias.map((cat) => (
            <SelectItem key={cat.id} value={cat.id}>
              {cat.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        data-testid="nombre-corto-input"
        placeholder="Nombre corto"
        value={nombreCorto}
        onChange={(e) => setNombreCorto(e.target.value)}
        className="w-32 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
      />
      <Button
        data-testid="add-categoria-btn"
        type="button"
        onClick={handleAdd}
        size="sm"
        className="bg-white text-slate-900 hover:bg-slate-200"
      >
        <Plus className="w-4 h-4" />
      </Button>
    </div>
  );
}

// Agregar al final del componente ClubesTab, antes del último cierre
// En el return principal, después del Dialog, agregar:
// {showJugadores && selectedClub && (
//   <JugadoresDialog club={selectedClub} onClose={() => {setShowJugadores(false); setSelectedClub(null);}} />
// )}
